import { flatten, maxBy, minBy, uniq } from 'lodash';

import { OperatingPhase } from '@/components/pages/EmissionsDashboard/store/operatingPhase';

import { Container_Insights_Energy_Usage_Expanded_V2_Bool_Exp } from '@/graphql/types';

import { EnergyUsageInput } from '@/hooks';

import { EmissionsDashboardStore } from './store';
import { ControlledAtmosphereTemperaturesFilter } from './temperatures';

export type EnergyUsageDataKey =
  | 'active_power_kW'
  | 'active_power_kW_stable'
  | 'active_power_kW_transition'
  | 'co2eq_kg_per_hour'
  | 'co2eq_kg_per_hour_stable'
  | 'co2eq_kg_per_hour_transition';

const getEnergyUsageExpandedV2QueryWhereClauseFromFilters = (
  snap: EmissionsDashboardStore,
): Container_Insights_Energy_Usage_Expanded_V2_Bool_Exp => {
  const {
    daterange,
    operatingPhase,
    selectedRefrigerationUnitModels,
    selectedTemperatures,
    selectedTradelanes,
    selectedAssets,
    selectedReeferAges,
  } = snap;

  let refrigeration_unit_model = {};
  if (selectedRefrigerationUnitModels.length > 0) {
    refrigeration_unit_model = {
      refrigeration_unit_model: {
        _in: flatten(
          selectedRefrigerationUnitModels.map((model) => model.name),
        ),
      },
    };
  }

  const selectedSetpointTemperatures = selectedTemperatures.filter(
    (temperature) =>
      temperature.id !== ControlledAtmosphereTemperaturesFilter.id,
  );
  let temperature_setpoint_id = {};
  if (selectedSetpointTemperatures.length > 0) {
    temperature_setpoint_id = {
      temperature_setpoint_id: {
        _in: selectedSetpointTemperatures.map((temperature) => temperature.id),
      },
    };
  }

  const controlledAtmosphereTemperature = selectedTemperatures.find(
    (temperature) =>
      temperature.id === ControlledAtmosphereTemperaturesFilter.id,
  );
  let controlled_atmosphere = {};
  if (
    controlledAtmosphereTemperature &&
    controlledAtmosphereTemperature.selected
  ) {
    controlled_atmosphere = {
      ca: {
        _eq: true,
      },
    };
  }

  let tradelane = {};
  if (selectedTradelanes.length > 0) {
    tradelane = {
      tradelane: {
        _in: selectedTradelanes.map((tradelane) => tradelane.label),
      },
    };
  }

  let reefer_ages = {};
  if (selectedReeferAges.length > 0) {
    reefer_ages = {
      age_id: {
        _in: selectedReeferAges.map((age) => age.id),
      },
    };
  }

  let mode = {};
  if (operatingPhase !== OperatingPhase.ALL) {
    mode = {
      mode: {
        _eq: operatingPhase,
      },
    };
  }

  let reefer_id = {};
  if (selectedAssets.length > 0) {
    reefer_id = {
      reefer_id: {
        _in: selectedAssets.map((asset) => asset.id),
      },
    };
  }

  const where: Container_Insights_Energy_Usage_Expanded_V2_Bool_Exp = {
    datetime: {
      _gte: daterange.from.toISOString(),
      _lte: daterange.to.toISOString(),
    },
    ...refrigeration_unit_model,
    ...temperature_setpoint_id,
    ...controlled_atmosphere,
    ...tradelane,
    ...mode,
    ...reefer_ages,
    ...reefer_id,
  };

  return where;
};

const getEnergyUsageInputFromFilters = (
  snap: EmissionsDashboardStore,
): EnergyUsageInput => {
  const {
    daterange,
    selectedRefrigerationUnitModels,
    selectedTemperatures,
    selectedTradelanes,
    selectedAssets,
    selectedReeferAges,
  } = snap;

  const params: EnergyUsageInput = {
    start: daterange.from.toISOString(),
    end: daterange.to.toISOString(),
  };

  // models
  if (selectedRefrigerationUnitModels.length > 0) {
    params.refrigeration_unit_models = flatten(
      selectedRefrigerationUnitModels.map((model) => model.name),
    );

    // name or label ?
    params.manufacturers = uniq(
      selectedRefrigerationUnitModels.map((model) => model.manufacturer.name),
    );
  }

  // setpoint temperature
  const selectedSetpointTemperatures = selectedTemperatures.filter(
    (temperature) =>
      temperature.id !== ControlledAtmosphereTemperaturesFilter.id,
  );
  if (selectedSetpointTemperatures.length > 0) {
    params.temperature_setpoint_ids = selectedSetpointTemperatures.map(
      (temperature) => temperature.id,
    );
  }

  // Controlled atmosphere
  const controlledAtmosphereTemperature = selectedTemperatures.find(
    (temperature) =>
      temperature.id === ControlledAtmosphereTemperaturesFilter.id,
  );
  if (
    controlledAtmosphereTemperature &&
    controlledAtmosphereTemperature.selected
  ) {
    params.ca = true;
  }

  // reefer ages
  if (selectedReeferAges.length > 0) {
    params.age_ids = selectedReeferAges.map((age) => age.id);
  }

  // tradelanes
  if (selectedTradelanes.length > 0) {
    params.tradelanes = selectedTradelanes.map((tradelane) => tradelane.label);
  }

  // reefer id
  if (selectedAssets.length > 0) {
    if (selectedAssets.length > 1) {
      console.warn(
        `getEnergyUsageInputFromFilters: only one asset id is supported for now`,
      );
    }
    params.reefer_id = selectedAssets.map((asset) => asset.id)[0];
  }

  return params;
};

export {
  getEnergyUsageExpandedV2QueryWhereClauseFromFilters,
  getEnergyUsageInputFromFilters,
};
